"use client";

// AuthProvider (C2) — session-state analog of reference/scopely-frontend/
// src/components/AuthProvider.tsx, scoped to the measured PD-1 contract.
// Deliberate divergences are catalogued in the module README ("Divergences
// from the reference"): no view-as role/profile preview, no favicon branding
// effect before #617 (now restored for validated local assets), no Google
// login, logout routes through authApi (F2 fetch fence)
// and stops the periodic-refresh timer. Expired-access recovery (the
// reference's 401 interceptor) lives in src/shared/api/request.ts, under
// every spine call, not here (#643).
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { authApi } from "../api/authApi";
import type { AuthUser, LoginResponse } from "../api/types";
import { parseExperienceProfile } from "../../../shared/experience/experienceProfile";

// Keep the browser boundary as narrow as the backend's imported-asset wire.
// A malformed client cache or API payload must not install an external icon.
const TENANT_FAVICON = /^\/media\/experience\/favicons\/[A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)*(?:\/[A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)*)*\.(?:png|ico|jpg|jpeg|webp)$/i;
const MANAGED_ICON = 'link[data-vip-tenant-favicon="true"]';

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login: (
    email: string,
    password: string,
    termsAccepted?: boolean,
  ) => Promise<LoginResponse>;
  loginWithUser: (user: AuthUser) => void;
  logout: () => void;
  isPlatformAdmin: boolean;
  isPlatformStaff: boolean;
  /** #527 (v2.36): the SERVER's `is_platform_staff` — the one predicate
   *  behind sessions/all/, the order faces' staff context and the API
   *  docs gate. Distinct from `isPlatformStaff` above, which is derived
   *  from the display `role` and cannot tell a platform admin WITH the
   *  staff flag from one without it (platform_admin outranks power_user
   *  in that vocabulary). Nav that leads to a staff-gated API gates on
   *  this, so it never shows a page the API would 403. */
  hasStaffGrant: boolean;
  isOrgAdmin: boolean;
  canAccessAdmin: boolean;
  /** The partner-portal nav gate (wire contract §9.3): true exactly when
   *  auth/me carries the `partner` block, which the server derives from
   *  the SAME predicate every portal endpoint gates on — so the nav can
   *  never show a portal the API would 403. */
  isPartnerUser: boolean;
  /** VIP's edit gate for partner settings controls: the block's
   *  can_manage. ZW2's resolve_channel_partner only admits the
   *  partner_admin role today, so this equals isPartnerUser until a
   *  member tier exists — but the gate reads the field the server
   *  reports, not that coincidence. */
  isPartnerAdmin: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

// Convenience projection for components that only render the user.
export function useUser(): AuthUser | null {
  return useAuth().user;
}

// 45 minutes — 75% of the reference's 1-hour access lifetime ("Fix #3" in
// the reference: refresh before expiry so long sessions never silently 401).
const REFRESH_INTERVAL_MS = 45 * 60 * 1000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const faviconUrl = parseExperienceProfile(user?.experience_profile)?.brand.favicon_url;

  useEffect(() => {
    const prior = document.head.querySelector<HTMLLinkElement>(MANAGED_ICON);
    if (!faviconUrl || !TENANT_FAVICON.test(faviconUrl)) {
      prior?.remove();
      return;
    }

    const icon = prior ?? document.createElement("link");
    icon.rel = "icon";
    icon.setAttribute("data-vip-tenant-favicon", "true");
    icon.setAttribute("href", faviconUrl);
    const keepTenantIconLast = () => {
      const icons = document.head.querySelectorAll('link[rel="icon"]');
      if (icons[icons.length - 1] !== icon) document.head.appendChild(icon);
    };
    keepTenantIconLast();
    // Next route metadata can insert a surface icon after login. Keep the
    // authenticated tenant icon last without replacing Next-owned nodes.
    const observer = new MutationObserver(keepTenantIconLast);
    observer.observe(document.head, { childList: true });
    return () => {
      observer.disconnect();
      icon.remove();
    };
  }, [faviconUrl]);

  const startPeriodicRefresh = useCallback(() => {
    if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
    refreshIntervalRef.current = setInterval(async () => {
      try {
        await authApi.refresh();
      } catch {
        // Refresh token expired or blacklisted — force re-login. The
        // reference keeps the interval firing after this; parity kept.
        setUser(null);
      }
    }, REFRESH_INTERVAL_MS);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // me → (401) → refresh → me: the reference bootstrap ladder. The
        // shared request primitive runs it for every call (#643).
        const me = await authApi.me();
        if (cancelled) return;
        setUser(me);
        startPeriodicRefresh();
      } catch {
        // Both tokens dead (or the retried me failed) — signed out.
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
    };
  }, [startPeriodicRefresh]);

  const login = useCallback(async (
    email: string,
    password: string,
    termsAccepted?: boolean,
  ) => {
    const result = await authApi.login(email, password, termsAccepted);
    // Cookies are set by the spine; app login completes only after the
    // forced-reset flow finishes (reference parity: a must_reset_password
    // user is held out of the session until the reset succeeds).
    if (!result.user.must_reset_password) {
      setUser(result.user);
      // A session that starts at the form is a session too: without this,
      // only a reload armed the timer and the access cookie lapsed at the
      // hour (UAT band 2, #643).
      startPeriodicRefresh();
    }
    return result;
  }, [startPeriodicRefresh]);

  const loginWithUser = useCallback((authUser: AuthUser) => {
    setUser(authUser);
    startPeriodicRefresh();
  }, [startPeriodicRefresh]);

  const logout = useCallback(() => {
    // Clear client state immediately for responsive UX; the server-side
    // blacklist is best-effort with one 500ms retry (reference parity,
    // routed through authApi — D3: the refresh token rides the httpOnly
    // cookie, so the body stays an empty object).
    setUser(null);
    if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
    authApi.logout().catch(() => {
      setTimeout(() => authApi.logout().catch(() => {}), 500);
    });
  }, []);

  const role = user?.role;
  const isPlatformStaff = role === "platform_admin" || role === "power_user";

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        loginWithUser,
        logout,
        isPlatformAdmin: role === "platform_admin",
        isPlatformStaff,
        hasStaffGrant: user?.is_platform_staff === true,
        isOrgAdmin: role === "org_admin",
        canAccessAdmin: isPlatformStaff,
        isPartnerUser: user?.partner != null,
        isPartnerAdmin: user?.partner?.can_manage === true,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
